import React, { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { fetchMeds } from "../../../integration/historico_meds/fetchMeds";
import { formatToBRL } from "brazilian-values";
import ModalVendas from "../../modals/vendas/Index";
import { BiSearch, BiRefresh } from "react-icons/bi";
import { GoAlert } from "react-icons/go";

const PedidosTabela = () => {
  const [filtros, setFiltros] = useState({ tipo: "id", valor: "" });
  const [pedidos, setPedidos] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const itensPorPagina = 10;
  const [pedidoAberto, setPedidoAberto] = useState(null);

  const funcVendas = async (usarFiltro = false) => {
    setCarregando(true);
    try {
      const params = {
        paginaAtual,
        itensPorPagina,
        id: null,
        id_transacao: null
      };

      if (usarFiltro && filtros.valor) {
        if (filtros.tipo === "id") params.id = filtros.valor;
        else if (filtros.tipo === "id_transacao")
          params.id_transacao = filtros.valor;
      }

      const resposta = await fetchMeds(params);
      const { dados } = resposta;

      setPedidos(dados.vendas || []);
      setTotalRegistros(dados.total || 0);
      setTotalPaginas(Math.ceil((dados.total || 0) / itensPorPagina));
    } catch (erro) {
      console.error("Erro ao buscar vendas:", erro);
      setPedidos([]);
      setTotalRegistros(0);
      setTotalPaginas(1);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    funcVendas();
  }, [paginaAtual]);

  const formatToDateTime = (dataStr) => {
    if (!dataStr) return "-";
    const data = new Date(dataStr);
    return data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const handleVerMais = (pedidoId) => {
    setPedidoAberto(pedidoAberto === pedidoId ? null : pedidoId);
  };

  const closeModal = () => setPedidoAberto(null);

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-[#fcfcfd] rounded-2xl">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-3xl font-light text-[#0c0c0c]">Infrações</h2>
        <span className="text-gray-500 text-sm">
          Total: <strong>{totalRegistros}</strong> registros
        </span>
      </div>

      {/* Filtros + Botões */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <select
          value={filtros.tipo}
          onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
          className="px-3 py-2 border text-gray-700 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9f8d72] outline-none"
        >
          <option value="id">ID</option>
          <option value="id_transacao">ID Transação</option>
        </select>

        <input
          type="text"
          placeholder={`Buscar por ${
            filtros.tipo === "id" ? "ID" : "ID Transação"
          }`}
          value={filtros.valor}
          onChange={(e) => setFiltros({ ...filtros, valor: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && funcVendas(true)}
          className="px-4 py-2 border text-gray-700 border-gray-300 rounded-lg w-80 focus:ring-2 focus:ring-[#9f8d72] outline-none"
        />

        <button
          onClick={() => funcVendas(true)}
          className="inline-flex items-center gap-2 px-5 py-2 bg-[#f5f4f1] text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-100 transition"
        >
          <BiSearch className="w-5 h-5" /> Pesquisar
        </button>

        <button
          onClick={() => {
            setFiltros({ tipo: "id", valor: "" });
            funcVendas(false);
          }}
          className="inline-flex items-center gap-2 px-5 py-2 bg-[#f5f4f1] text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-100 transition"
        >
          <BiRefresh className="w-5 h-5" /> Atualizar Histórico
        </button>
      </div>

      {/* Tabela ou mensagem */}
      {carregando ? (
        <div className="text-center py-4 text-gray-500">Carregando...</div>
      ) : pedidos.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
              <thead className="bg-[#f5f4f1] text-gray-700 font-semibold">
                <tr>
                  {[
                    "Criado em",
                    "Id",
                    "Cliente",
                    "Valor",
                    "Status",
                    "Ações"
                  ].map((title) => (
                    <th key={title} className="px-4 py-3 text-left">
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pedidos.map((pedido) => (
                  <tr key={pedido.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      {formatToDateTime(pedido.createdAt)}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {pedido.idtransaction?.length > 15
                        ? `${pedido.idtransaction.slice(0, 15)}...`
                        : pedido.idtransaction}
                    </td>
                    <td className="px-4 py-3">
                      {pedido.customer?.name || "-"}
                    </td>
                    <td className="px-4 py-3 font-semibold">
                      {formatToBRL(pedido.amount / 100)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-300 text-white">
                        <GoAlert className="w-4 h-4" /> MED
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleVerMais(pedido.id)}
                        className="inline-flex items-center gap-1 text-[#9f8d72] hover:text-[#9f8d72] transition text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" /> Ver mais
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="mt-6 flex justify-between items-center text-sm text-gray-700">
            <div>
              Página <strong>{paginaAtual}</strong> de{" "}
              <strong>{totalPaginas}</strong>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPaginaAtual(Math.max(1, paginaAtual - 1))}
                disabled={paginaAtual === 1}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
              >
                Anterior
              </button>
              <button
                onClick={() =>
                  setPaginaAtual(Math.min(totalPaginas, paginaAtual + 1))
                }
                disabled={paginaAtual === totalPaginas}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
              >
                Próxima
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-gray-500 flex flex-col items-center gap-2">
          <GoAlert className="w-6 h-6 text-yellow-500" />
          Nenhum resultado encontrado.
        </div>
      )}

      {pedidoAberto && (
        <ModalVendas
          data={pedidos.find((p) => p.id === pedidoAberto)}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default PedidosTabela;
